import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { companyInfo } from '../../mock';
import Footer from '../../components/Footer';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-dark)' }}>
      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(9, 166, 163, 0.2)', backgroundColor: 'rgba(36, 37, 38, 0.95)' }}>
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-accent-teal hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Terms and Conditions</h1>
        
        <div className="text-neutral-silver leading-relaxed space-y-6">
          <p className="text-lg">Last updated: December 2024</p>

          <p>
            Welcome to NT Studios. By accessing and using our website and AI-powered tools, you agree to comply with and be bound by the following terms and conditions.
          </p>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">1. Acceptance of Terms</h2>
            <p>
              By accessing or using NT Studios' services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">2. Services Provided</h2>
            <p className="mb-4">NT Studios provides:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Website development and design services</li>
              <li>Web application development</li>
              <li>Game development services</li>
              <li>Educational game creation</li>
              <li>AI-powered tools (Email Generator, Content Brief Generator, Competitor Analysis)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">3. User Accounts</h2>
            <p className="mb-4">
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">4. Subscription and Payment</h2>
            <p className="mb-4">
              Our AI tools are available through subscription plans processed via <strong className="text-accent-teal">Razorpay</strong>:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Free Plan:</strong> 10 uses per day per tool</li>
              <li><strong>Premium Plans:</strong> Increased usage limits with monthly/annual billing</li>
              <li>All prices are listed in INR and exclude applicable taxes</li>
              <li>Subscriptions auto-renew unless cancelled before the renewal date</li>
              <li>Payment information is securely processed by Razorpay</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">5. Acceptable Use</h2>
            <p className="mb-4">You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use our services for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt our services</li>
              <li>Use automated systems to access our services excessively</li>
              <li>Resell or redistribute content generated by our tools</li>
              <li>Reverse engineer or attempt to extract source code</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">6. Intellectual Property</h2>
            <p className="mb-4">
              All content, features, and functionality of NT Studios services are owned by us and protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              Content generated using our AI tools: You retain ownership of the content you generate, but you grant us a license to use anonymized data to improve our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">7. AI-Generated Content</h2>
            <p className="mb-4">
              Our AI tools use advanced language models to generate content. You acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>AI-generated content should be reviewed and edited before use</li>
              <li>We do not guarantee accuracy or suitability for specific purposes</li>
              <li>You are responsible for verifying and validating generated content</li>
              <li>Generated content may not always be unique</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">8. Limitation of Liability</h2>
            <p>
              NT Studios shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services. Our total liability shall not exceed the amount you paid for the service in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">9. Service Availability</h2>
            <p>
              We strive to maintain 99.9% uptime but do not guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue any part of our services with or without notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">10. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account immediately, without prior notice, for conduct that violates these Terms or is harmful to other users, us, or third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or through our website. Continued use of our services after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in [Your City], India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Contact Information</h2>
            <p>
              For questions about these Terms and Conditions:
            </p>
            <ul className="list-none space-y-2 mt-4">
              <li>Email: <a href="mailto:legal@ntstudios.com" className="text-accent-teal hover:underline">legal@ntstudios.com</a></li>
              <li>Contact Form: <Link to="/#contact" className="text-accent-teal hover:underline">ntstudios.com/#contact</Link></li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
